import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, Receipt, Wallet, ChevronRight, ChevronLeft, AlertCircle, ChevronDown, Gift, FileText, Smartphone, Bluetooth, Check, Send } from 'lucide-react';
import { createPayment } from '../lib/payments';
import { useBookingInvoice } from '../hooks/useBookingInvoice';
import { supabase } from '../lib/supabaseClient';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useAppSystem } from '../hooks/useAppSystem';
import { useModalZIndex } from '../contexts/ModalStackContext';
import { GiftCardService } from '../services/giftCardService';
import { runTapToPayFlow, isTapToPayAvailable } from '../services/tapToPayService';
import * as readerService from '../services/stripeReaderService';
import type { ReaderInterface } from '@capacitor-community/stripe-terminal';
import StripeCardEntry from './StripeCardEntry';
// Using proven invoice payment link system for in-person payments

interface RecordPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (paymentId?: string) => void;
  bookingId?: string;
  proposalId?: string;
  projectId?: string;
  /** Standalone invoice to settle (e.g. customer orders / counter sales).
   *  Balance is read live from the invoice; payments carry invoice_id so the
   *  allocation trigger applies them automatically. */
  invoiceId?: string;
  productSaleId?: string;
  businessId: string;
  bookingTotal: number;
  amountPaid: number;
  giftCardId?: string;
  initialDepositAmount?: number | null;
  customerId?: string;
  /**
   * Optional. When provided, after a successful payment the modal shows a
   * "Payment Successful" screen with a Send Invoice button. Tapping it closes
   * this modal and invokes the callback (typically opens BetaPaymentsModal so
   * the user can email/SMS an invoice).
   */
  onSendInvoice?: () => void;
}

const manualPaymentMethods = [
  {
    id: 'credit_card',
    name: 'Record Credit / Debit',
    icon: <CreditCard className="h-5 w-5 text-purple-500" />,
    description: 'Record credit or debit card payment'
  },
  {
    id: 'bank_transfer',
    name: 'Record Bank Transfer',
    icon: <Receipt className="h-5 w-5 text-green-500" />,
    description: 'Record direct bank transfer payment'
  },
  {
    id: 'cash',
    name: 'Record Cash',
    icon: <Wallet className="h-5 w-5 text-yellow-500" />,
    description: 'Record cash payment'
  }
];

const stripePaymentMethod = {
  id: 'stripe',
  name: 'Stripe Pay',
  icon: <img src="/Stripe (2).png" alt="Stripe" className="h-10 w-10" />,
  description: 'Process payment through Stripe'
};

const giftCardPaymentMethod = {
  id: 'gift_card',
  name: 'Record Gift Card',
  icon: <Gift className="h-5 w-5 text-blue-500" />,
  description: 'Redeem gift card for payment'
};

export default function RecordPaymentModal({
  isOpen,
  onClose,
  onSuccess,
  bookingId,
  proposalId,
  projectId,
  invoiceId,
  productSaleId,
  businessId,
  bookingTotal: bookingTotalProp,
  amountPaid: amountPaidProp,
  giftCardId,
  initialDepositAmount,
  customerId,
  onSendInvoice,
}: RecordPaymentModalProps) {
  const { coreApps, installedAddons } = useAppSystem();
  // Stacks above a parent modal (e.g. elevated BookingInfoModal) when nested;
  // otherwise falls back to the historical default z-index.
  const stackBackdropZ = useModalZIndex(100060);
  // Prefer the new invoice's total/paid (cents-accurate rollup) when one exists,
  // so the remaining-balance math is correct from every entry point. Falls back
  // to the props for proposals/legacy bookings with no invoice.
  const { invoice: liveInvoice } = useBookingInvoice(bookingId);
  // Standalone invoice mode (orders/counter sales): read the balance directly.
  const [standaloneInvoice, setStandaloneInvoice] = useState<{ total_cents: number; amount_paid_cents: number } | null>(null);
  useEffect(() => {
    if (!invoiceId || !isOpen) { setStandaloneInvoice(null); return; }
    (async () => {
      const { data } = await supabase
        .from('invoices')
        .select('total_cents, amount_paid_cents')
        .eq('id', invoiceId)
        .maybeSingle();
      if (data) setStandaloneInvoice(data as { total_cents: number; amount_paid_cents: number });
    })();
  }, [invoiceId, isOpen]);
  const effectiveInvoice = standaloneInvoice || liveInvoice;
  const bookingTotal = effectiveInvoice ? effectiveInvoice.total_cents / 100 : bookingTotalProp;
  const amountPaid = effectiveInvoice ? effectiveInvoice.amount_paid_cents / 100 : amountPaidProp;
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState(manualPaymentMethods[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [paymentType, setPaymentType] = useState<'manual' | 'stripe' | 'gift_card' | 'wallet' | null>(null);
  const [readerFlowOpen, setReaderFlowOpen] = useState(false);
  const [readerCardType, setReaderCardType] = useState<'debit' | 'credit' | null>(null);
  // Tap to Pay lives INSIDE Stripe Pay (it's a Stripe Terminal method, not its
  // own payment type) — tapping its card starts the charge immediately.
  const [tapCharging, setTapCharging] = useState(false);

  // Post-payment success screen state
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [successAmount, setSuccessAmount] = useState<number>(0);
  const [successMethodLabel, setSuccessMethodLabel] = useState<string>('');
  const [amount, setAmount] = useState('');
  const [stripeConnectEnabled, setStripeConnectEnabled] = useState(false);
  const [tapToPayAvailable, setTapToPayAvailable] = useState(false);
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  const [surchargeEnabled, setSurchargeEnabled] = useState(false);
  const [surchargePercentage, setSurchargePercentage] = useState(0);
  
  // Tab state: 'payment' (full payment) or 'deposit' (partial payment)
  const [activeTab, setActiveTab] = useState<'payment' | 'deposit'>('payment');
  const [depositAmount, setDepositAmount] = useState('');
  const [selectedPercentage, setSelectedPercentage] = useState<'25' | '50' | '75' | 'full' | null>(null);
  
  // Gift card state
  const [giftCardAppInstalled, setGiftCardAppInstalled] = useState(false);
  const [giftCardCode, setGiftCardCode] = useState('');
  const [validatedGiftCard, setValidatedGiftCard] = useState<any>(null);
  const [giftCardBalance, setGiftCardBalance] = useState<number | null>(null);
  const [giftCardRedemptionAmount, setGiftCardRedemptionAmount] = useState('');
  const [validatingGiftCard, setValidatingGiftCard] = useState(false);
  const [giftCardError, setGiftCardError] = useState<string | null>(null);

  // Wallet state
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [walletRedemptionAmount, setWalletRedemptionAmount] = useState('');
  const [walletRedemptionMode, setWalletRedemptionMode] = useState<'full' | 'custom'>('full');

  /** Saved card on file (Stripe Connect) for “charge card on file” */
  const [savedCardOnFile, setSavedCardOnFile] = useState<{
    id: string;
    card_brand: string | null;
    card_last4: string | null;
    card_exp_month: number | null;
    card_exp_year: number | null;
    /** From Stripe pm.card.funding — used to show exact processing fee */
    card_funding?: string | null;
  } | null>(null);
  const [savedCardLoading, setSavedCardLoading] = useState(false);
  /** Dedicated UI + disabled state for “Charge card on file” (paired with ref for instant double-click guard). */
  const [savedCardCharging, setSavedCardCharging] = useState(false);
  /** Stripe Pay: optional save new card to customer after successful charge. */
  const [saveNewCardOnFile, setSaveNewCardOnFile] = useState(false);
  /** Prevents double-submit before React re-renders (multi-click / double-tap). */
  const savedCardChargeInFlightRef = useRef(false);

  // Card Reader (Bluetooth) state
  const [readerStatus, setReaderStatus] = useState<readerService.ReaderStatus>('idle');
  const [readerDisplayMessage, setReaderDisplayMessage] = useState<string>('');
  const [discoveredReaders, setDiscoveredReaders] = useState<ReaderInterface[]>([]);
  const [readerError, setReaderError] = useState<string | null>(null);
  const [connectedReader, setConnectedReader] = useState<ReaderInterface | null>(null);
  const [connectStartedAt, setConnectStartedAt] = useState<number | null>(null);
  const [connectElapsedSec, setConnectElapsedSec] = useState(0);
  const [firmwareProgress, setFirmwareProgress] = useState<number | null>(null);
  const [firmwareUpdating, setFirmwareUpdating] = useState(false);
  /** Guards against re-triggering the auto-connect attempt within a single Stripe-Pay session. */
  const autoConnectAttemptedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      savedCardChargeInFlightRef.current = false;
      setSavedCardCharging(false);
      setSaveNewCardOnFile(false);
      setIsAnimating(true);
      const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
      
      // Set amount based on active tab
      if (activeTab === 'payment') {
        // Full payment - set to remaining balance
      setAmount(remainingBalance.toFixed(2));
      } else {
        // Deposit - use initialDepositAmount if provided, otherwise keep existing deposit amount
        if (initialDepositAmount && initialDepositAmount > 0) {
          setDepositAmount(initialDepositAmount.toFixed(2));
          setAmount(initialDepositAmount.toFixed(2));
        } else if (!depositAmount) {
          setDepositAmount('');
          setAmount('');
        } else {
          setAmount(depositAmount || '');
        }
      }
      
      // Reset form state
      setSelectedMethod(manualPaymentMethods[0]);
      setPaymentType(null);
      setIsDropdownOpen(false);
      setError(null);
      // NOTE: post-payment success screen reset moved to the "modal closes" effect
      // so that a successful payment doesn't wipe its own success screen when the
      // parent refreshes booking data (which updates amountPaid/bookingTotal props
      // and re-fires this effect).
      
      // Reset gift card state
      setGiftCardCode('');
      setValidatedGiftCard(null);
      setGiftCardBalance(null);
      setGiftCardRedemptionAmount('');
      setGiftCardError(null);

      // Reset wallet state
      setWalletBalance(0);
      setWalletRedemptionAmount('');
      setWalletRedemptionMode('full');
      
      // Reset deposit state (unless initialDepositAmount is provided)
      if (!initialDepositAmount || initialDepositAmount <= 0) {
        setDepositAmount('');
        setSelectedPercentage(null);
      }
      
      // Check if Stripe Connect is enabled (simple check like invoice system)
      checkStripeConnectStatus();
      isTapToPayAvailable().then(setTapToPayAvailable);
      import('@capacitor/core').then(({ Capacitor }) => {
        setIsNativePlatform(Capacitor.isNativePlatform());
      }).catch(() => setIsNativePlatform(false));
      // Check if gift card app is installed
      checkGiftCardAppInstalled();
      // Load wallet balance
      loadWalletBalance();

      // Reset card reader UI state (module may still hold connection until cleanup)
      setReaderStatus('idle');
      setReaderDisplayMessage('');
      setReaderError(null);
      setDiscoveredReaders([]);
      setConnectedReader(readerService.getConnectedReader());
    }
  }, [isOpen, bookingTotal, amountPaid, businessId, activeTab, initialDepositAmount]);

  // On modal close, KEEP the reader connected (and the SDK alive) so the next payment is instant —
  // we only detach the React UI callbacks. The BLE connection persists for the app session; it's only
  // torn down on explicit disconnect or full cleanup. This is what makes reconnect feel instant.
  useEffect(() => {
    if (!isOpen) {
      readerService.removeCallbacks();
      // Don't null connectedReader here — the service still holds the live connection and we re-read
      // it on open. We only clear transient discovery/error UI.
      setDiscoveredReaders([]);
      setReaderError(null);
      setReaderDisplayMessage('');
      setReaderFlowOpen(false);
      setReaderCardType(null);
      // Reset the post-payment success screen here (not in the open-effect) so a
      // refreshBooking-triggered prop change doesn't immediately dismiss it.
      setPaymentSuccess(false);
      setSuccessAmount(0);
      setSuccessMethodLabel('');
    }
  }, [isOpen]);

  // Reset card type selection whenever the connected reader changes (e.g., disconnect)
  useEffect(() => {
    if (!connectedReader) setReaderCardType(null);
  }, [connectedReader]);

  // Poll the reader service every second so the UI always reflects live state,
  // even if React callback registration was unmounted/replaced.
  useEffect(() => {
    if (!isOpen || paymentType !== 'stripe') return;
    const tick = setInterval(() => {
      const snap = readerService.getReaderSnapshot();
      setReaderStatus(snap.status);
      setReaderDisplayMessage(snap.displayMessage);
      setFirmwareProgress(snap.firmwareProgress);
      setFirmwareUpdating(snap.firmwareUpdating);
      if (snap.status === 'error' && snap.statusMessage) {
        setReaderError(snap.statusMessage);
      }
      setConnectedReader(snap.connectedReader);
      if (connectStartedAt) {
        setConnectElapsedSec(Math.floor((Date.now() - connectStartedAt) / 1000));
      } else {
        setConnectElapsedSec(0);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [isOpen, paymentType, connectStartedAt]);

  // Register reader status callbacks
  useEffect(() => {
    if (isOpen && paymentType === 'stripe') {
      readerService.onStatusChange((status, message) => {
        setReaderStatus(status);
        if (status === 'error') {
          setReaderError(message || 'An error occurred');
        } else if (status !== 'idle' || !message) {
          setReaderError(null);
        }
        if (status === 'connected' || status === 'idle' || status === 'success' || status === 'error') {
          setConnectedReader(readerService.getConnectedReader());
        }
      });
      readerService.onReaderChange((reader) => {
        setConnectedReader(reader);
      });
      readerService.onDisplayMessage((event) => {
        setReaderDisplayMessage(event.message);
      });
      readerService.onDiscoveredReaders((readers) => {
        setDiscoveredReaders(readers);
        setReaderError(null);
      });
      setConnectedReader(readerService.getConnectedReader());
      return () => { readerService.removeCallbacks(); };
    }
  }, [isOpen, paymentType]);

  // Silent reader pre-warm: if this business has paired a reader before, start
  // reconnecting in the background the moment the modal opens so the Card
  // Reader card shows "connected" instantly when tapped. Selecting Stripe Pay
  // no longer auto-connects — the reader is an explicit choice inside it.
  useEffect(() => {
    if (!isOpen) {
      autoConnectAttemptedRef.current = false;
      return;
    }
    if (!isNativePlatform || !businessId) return;
    if (!readerService.getRememberedReaderSerial(businessId)) return;
    if (autoConnectAttemptedRef.current) return;

    // Already connected (persisted from a previous payment this session) → instant, nothing to do.
    if (readerService.isReaderConnected()) {
      setConnectedReader(readerService.getConnectedReader());
      return;
    }

    autoConnectAttemptedRef.current = true;
    (async () => {
      try {
        const result = await readerService.autoConnectReader(businessId);
        if (result.outcome === 'connected') {
          setConnectedReader(readerService.getConnectedReader());
        }
        // Pre-warm is silent: no error UI unless the user explicitly taps Card Reader.
      } catch {
        // Silent — the Card Reader card retries on demand.
      }
    })();
  }, [isOpen, isNativePlatform, businessId]);

  // Explicit reader connect from the Card Reader card inside Stripe Pay.
  const handleConnectReader = async () => {
    setReaderFlowOpen(true);
    setReaderError(null);
    setDiscoveredReaders([]);
    try {
      const result = await readerService.autoConnectReader(businessId);
      if (result.outcome === 'connected') {
        setConnectedReader(readerService.getConnectedReader());
      } else if (result.outcome === 'needs-selection') {
        // Multiple unknown readers — let the cashier pick. List already populated via callback.
        setDiscoveredReaders(result.readers);
      } else {
        setReaderError(
          'No readers found. Check: (1) WisePad 3 is on and charged, (2) iPhone Bluetooth is on, (3) Settings → Privacy & Security → Bluetooth → enable this app, (4) reader is registered in Stripe Dashboard → Terminal → Readers for your Connect account, (5) reader is not connected to another phone.'
        );
      }
    } catch (err: any) {
      setReaderError(err?.message || 'Failed to connect to reader');
    }
  };

  // Tap to Pay: tapping the card inside Stripe Pay charges immediately —
  // the phone IS the reader, so there's nothing else to configure.
  const handleTapToPayCharge = async () => {
    if (tapCharging || loading) return;
    const chargeAmount = activeTab === 'deposit' && depositAmount
      ? parseFloat(depositAmount) || 0
      : parseFloat(amount) || 0;
    if (!chargeAmount || chargeAmount <= 0) {
      setError('Enter a valid amount');
      return;
    }
    setTapCharging(true);
    setError(null);
    try {
      const { paymentIntentId } = await runTapToPayFlow({
        businessId,
        amount: chargeAmount,
        paymentType: activeTab === 'deposit' ? 'deposit' : 'final',
        bookingId: bookingId || undefined,
        proposalId: proposalId || undefined,
        description: 'Booking payment',
      });
      const { data: payment, error: paymentError } = await supabase
        .from('payments')
        .insert({
          business_id: businessId,
          booking_id: bookingId || null,
          proposal_id: proposalId || null,
          invoice_id: invoiceId || null,
          product_sale_id: productSaleId || null,
          amount: chargeAmount,
          method: 'credit_card',
          channel: 'card_tap_to_pay',
          status: 'completed',
          stripe_payment_intent_id: paymentIntentId,
          reference: paymentIntentId,
          notes: 'Tap to Pay on iPhone',
          payment_type: activeTab === 'deposit' ? 'deposit' : 'final',
        })
        .select()
        .single();
      if (paymentError) {
        console.error('Failed to create payment record:', paymentError);
        await triggerSuccess(paymentIntentId, chargeAmount, 'Tap to Pay on iPhone');
      } else {
        await triggerSuccess(payment.id, chargeAmount, 'Tap to Pay on iPhone');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Tap to Pay failed';
      // Closing the Tap to Pay sheet cancels the collect command — that's the
      // cashier backing out, not a failure worth an error banner.
      if (!/cancel/i.test(msg)) setError(msg);
    } finally {
      setTapCharging(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isOpen || !customerId || !businessId) {
      setSavedCardOnFile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setSavedCardLoading(true);
      try {
        const { data, error } = await supabase
          .from('customer_saved_cards')
          .select('id, card_brand, card_last4, card_exp_month, card_exp_year, card_funding')
          .eq('customer_id', customerId)
          .eq('business_id', businessId)
          .maybeSingle();
        if (cancelled) return;
        if (!error && data) setSavedCardOnFile(data);
        else setSavedCardOnFile(null);
      } catch {
        if (!cancelled) setSavedCardOnFile(null);
      } finally {
        if (!cancelled) setSavedCardLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, customerId, businessId]);

  // Centralized success handler — replaces the previous "onSuccess() + setTimeout(onClose)" pattern.
  // Notifies the parent (so booking data refreshes), then displays the success screen with Send + Close.
  const triggerSuccess = async (
    paymentId: string,
    amountCharged: number,
    methodLabel: string,
  ) => {
    try {
      await onSuccess(paymentId);
    } catch (err) {
      console.error('onSuccess handler failed:', err);
    }
    setSuccessAmount(amountCharged);
    setSuccessMethodLabel(methodLabel);
    setLoading(false);
    setPaymentSuccess(true);
  };

  const checkStripeConnectStatus = async () => {
    try {
      const { data: business, error } = await supabase
        .from('businesses')
        .select('stripe_connect_account_id, stripe_connect_charges_enabled, credit_card_surcharge_enabled, credit_card_surcharge_percentage')
        .eq('id', businessId)
        .single();

      if (error || !business) {
        console.error('❌ Failed to fetch business Stripe Connect status:', error);
        setStripeConnectEnabled(false);
        return;
      }

      const isEnabled = business.stripe_connect_account_id && business.stripe_connect_charges_enabled;
      setStripeConnectEnabled(isEnabled);
      setSurchargeEnabled(business.credit_card_surcharge_enabled ?? false);
      setSurchargePercentage(business.credit_card_surcharge_percentage ?? 0);
      
      if (isEnabled) {
        console.log('✅ Stripe Connect enabled for business');
      } else {
        console.log('❌ Stripe Connect not properly configured');
      }
    } catch (error) {
      console.error('❌ Error checking Stripe Connect status:', error);
      setStripeConnectEnabled(false);
    }
  };

  const checkGiftCardAppInstalled = () => {
    const allApps = [...coreApps, ...installedAddons];
    const hasGiftCardApp = allApps.some(
      app => app.app?.slug === 'gift-cards' && app.status === 'active'
    );
    setGiftCardAppInstalled(hasGiftCardApp);
  };

  useEffect(() => {
    checkGiftCardAppInstalled();
  }, [coreApps, installedAddons]);

  const loadWalletBalance = async () => {
    if (!customerId || !supabase) {
      setWalletBalance(0);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('wallet')
        .eq('id', customerId)
        .single();
      if (!error && data) {
        setWalletBalance(parseFloat(String(data.wallet || 0)) || 0);
      }
    } catch {
      setWalletBalance(0);
    }
  };

  const getStripeBaseChargeAmount = () => {
    if (activeTab === 'deposit' && depositAmount) {
      return parseFloat(depositAmount) || 0;
    }
    return parseFloat(amount) || 0;
  };

  /** Exact fee / total for the saved card (matches server charge-customer-saved-card logic). */
  const savedCardChargePreview = React.useMemo(() => {
    const base =
      activeTab === 'deposit' && depositAmount
        ? parseFloat(depositAmount) || 0
        : parseFloat(amount) || 0;
    const pct = surchargePercentage || 0;
    const feeIfCredit =
      surchargeEnabled && pct > 0 ? Math.round(base * (pct / 100) * 100) / 100 : 0;
    const funding = savedCardOnFile?.card_funding;

    if (!Number.isFinite(base) || base <= 0) {
      return { buttonLabel: 'Charge card on file', subline: null as string | null };
    }

    if (!surchargeEnabled || pct <= 0 || feeIfCredit <= 0) {
      return {
        buttonLabel: `Charge card on file — $${base.toFixed(2)}`,
        subline: null as string | null,
      };
    }

    if (funding === 'credit') {
      const total = Math.round((base + feeIfCredit) * 100) / 100;
      return {
        buttonLabel: `Charge card on file — $${total.toFixed(2)}`,
        subline: `Includes $${feeIfCredit.toFixed(2)} processing fee (${pct}% on $${base.toFixed(2)})`,
      };
    }

    if (funding === 'debit' || funding === 'prepaid') {
      return {
        buttonLabel: `Charge card on file — $${base.toFixed(2)}`,
        subline: 'No processing fee (debit or prepaid card)',
      };
    }

    const totalIfCredit = Math.round((base + feeIfCredit) * 100) / 100;
    return {
      buttonLabel: `Charge card on file — $${base.toFixed(2)}`,
      subline: `If this card is credit: add $${feeIfCredit.toFixed(2)} (${pct}%), total $${totalIfCredit.toFixed(2)}. Debit/prepaid: $${base.toFixed(2)}.`,
    };
  }, [
    activeTab,
    amount,
    depositAmount,
    surchargeEnabled,
    surchargePercentage,
    savedCardOnFile?.card_funding,
  ]);

  const SYSTEM_ERROR_MESSAGE = 'System error. Please try again in a moment.';

  /**
   * What the saved-card function said. Payment errors (declines, expired card,
   * no card on file) are shown as-is. Anything else is a system error; the
   * detail goes to the console so it can be traced.
   */
  const readSavedCardChargeError = async (
    error: unknown,
    payload: Record<string, unknown> | null,
  ): Promise<string> => {
    let body: Record<string, unknown> | null = payload;
    if (!body && error instanceof FunctionsHttpError && error.context) {
      try {
        const text = await error.context.text();
        try {
          body = JSON.parse(text) as Record<string, unknown>;
        } catch {
          body = { error: text };
        }
      } catch {
        body = null;
      }
    }
    const kind = typeof body?.kind === 'string' ? body.kind : null;
    const message = typeof body?.error === 'string' ? body.error : null;
    console.error('charge-customer-saved-card failed', {
      kind,
      code: body?.code ?? null,
      decline_code: body?.decline_code ?? null,
      message,
      status: error instanceof FunctionsHttpError ? error.context?.status : null,
      error,
    });
    if (kind === 'payment' && message) return message;
    return SYSTEM_ERROR_MESSAGE;
  };

  const handleChargeSavedCard = async () => {
    if (!customerId || !savedCardOnFile) return;
    const base = getStripeBaseChargeAmount();
    if (!base || base <= 0) {
      setError('Enter a valid amount');
      return;
    }
    if (savedCardChargeInFlightRef.current || loading || savedCardCharging) return;
    savedCardChargeInFlightRef.current = true;
    setSavedCardCharging(true);
    setLoading(true);
    setError(null);
    try {
      await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('charge-customer-saved-card', {
        body: {
          businessId,
          customerId,
          baseAmount: base,
          bookingId: bookingId || undefined,
          proposalId: proposalId || undefined,
          paymentType: activeTab === 'deposit' ? 'deposit' : 'final',
        },
      });
      const raw = data as Record<string, unknown> | null;
      const payload =
        raw && typeof raw === 'object' && 'data' in raw && raw.data && typeof raw.data === 'object'
          ? (raw.data as Record<string, unknown>)
          : raw;
      if (error || (payload?.error && typeof payload.error === 'string')) {
        throw new Error(await readSavedCardChargeError(error, error ? null : payload));
      }
      const piId = payload?.paymentIntentId as string | undefined;
      const total = Number(payload?.totalAmount);
      const sur = Number(payload?.surchargeAmount ?? 0);
      if (!piId || !Number.isFinite(total)) {
        console.error('charge-customer-saved-card: unexpected response', payload);
        throw new Error(SYSTEM_ERROR_MESSAGE);
      }

      // Always persist the pre-surcharge amount we charged against (avoids bad base_amount if response shape changes).
      const baseForRow = Math.round(base * 100) / 100;

      const { data: payment, error: paymentError } = await supabase
        .from('payments')
        .insert({
          business_id: businessId,
          booking_id: bookingId || null,
          proposal_id: proposalId || null,
          project_id: projectId || null,
          invoice_id: invoiceId || null,
          product_sale_id: productSaleId || null,
          amount: total,
          base_amount: baseForRow,
          surcharge_amount: sur,
          method: 'credit_card',
          channel: 'card_online',
          status: 'completed',
          stripe_payment_intent_id: piId,
          reference: piId,
          notes:
            sur > 0
              ? `Saved card on file (includes $${sur.toFixed(2)} processing fee)`
              : 'Saved card on file',
          payment_type: activeTab === 'deposit' ? 'deposit' : 'final',
        })
        .select()
        .single();

      if (paymentError) {
        console.error('Failed to create payment record:', paymentError);
        await triggerSuccess(piId, total, 'Saved Card on File');
      } else {
        await triggerSuccess(payment.id, total, 'Saved Card on File');
      }
    } catch (err: unknown) {
      if (!(err instanceof Error)) console.error('charge-customer-saved-card: unexpected failure', err);
      setError(err instanceof Error ? err.message : SYSTEM_ERROR_MESSAGE);
    } finally {
      savedCardChargeInFlightRef.current = false;
      setSavedCardCharging(false);
      setLoading(false);
    }
  };

  const handleWalletRedemption = async () => {
    if (!customerId || !supabase) {
      setError('Customer is required for wallet redemption');
      return;
    }

    const redemptionAmount = walletRedemptionMode === 'full'
      ? Math.min(walletBalance, Math.round((bookingTotal - amountPaid) * 100) / 100)
      : parseFloat(walletRedemptionAmount);

    if (isNaN(redemptionAmount) || redemptionAmount <= 0) {
      setError('Please enter a valid redemption amount');
      return;
    }

    const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
    if (redemptionAmount > remainingBalance) {
      setError(`Amount cannot exceed remaining balance ($${remainingBalance.toFixed(2)})`);
      return;
    }

    if (redemptionAmount > walletBalance) {
      setError(`Amount cannot exceed wallet balance ($${walletBalance.toFixed(2)})`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Deduct from wallet
      const newWalletBalance = walletBalance - redemptionAmount;
      const { error: walletError } = await supabase
        .from('customers')
        .update({ wallet: newWalletBalance })
        .eq('id', customerId);

      if (walletError) throw walletError;

      // Log wallet transaction (negative amount for deduction)
      await supabase.from('wallet_transactions').insert({
        business_id: businessId,
        customer_id: customerId,
        amount: -redemptionAmount,
        type: 'manual',
        description: `Wallet redemption for ${bookingId ? 'booking' : 'payment'}`,
        reference_id: bookingId || proposalId || invoiceId || null,
      });

      // Create payment record
      const payment = await createPayment({
        business_id: businessId,
        booking_id: bookingId || null,
        proposal_id: proposalId || null,
        project_id: projectId || null,
        gift_card_id: null,
        invoice_id: invoiceId || null,
        product_sale_id: productSaleId || null,
        amount: redemptionAmount,
        method: 'other',
        channel: 'wallet',
        status: 'completed',
        reference: 'Wallet Balance',
        notes: `Wallet redemption - $${redemptionAmount.toFixed(2)}`,
        payment_type: activeTab === 'deposit' ? 'deposit' : 'final'
      });

      await triggerSuccess(payment.id, redemptionAmount, 'Wallet Balance');
    } catch (err) {
      console.error('Error processing wallet redemption:', err);
      setError(err instanceof Error ? err.message : 'Failed to process wallet redemption');
    } finally {
      setLoading(false);
    }
  };

  // Simple check for Stripe Connect - no complex initialization needed

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setSelectedMethod(manualPaymentMethods[0]);
      setAmount('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For gift card payments, giftCardId may be null initially (will be linked after gift card creation)
    // For booking payments, bookingId is required
    if (!bookingId && !giftCardId) {
      // Allow payment creation without context for now (will be linked later)
      // This is needed for gift card payments where we create payment first, then gift card
    }

    // For Stripe payments, the StripePaymentForm handles the submission
    if (selectedMethod.id === 'stripe' || paymentType === 'stripe') {
      return;
    }

    // For gift card payments, handleGiftCardRedemption handles the submission
    if (paymentType === 'gift_card') {
      return;
    }

    // Validate amount
    const paymentAmount = parseFloat(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
    if (paymentAmount > remainingBalance) {
      setError(`Payment amount cannot exceed remaining balance ($${remainingBalance.toFixed(2)})`);
      return;
    }

    try {
      setLoading(true);

      console.log('🎯 [UI DEBUG] User clicked "Record Payment" button');
      console.log('🎯 [UI DEBUG] Payment form data:', {
        bookingId,
        giftCardId,
        amount: paymentAmount,
        method: selectedMethod.id,
        status: 'completed',
        payment_type: activeTab === 'deposit' ? 'deposit' : 'final'
      });

      const payment = await createPayment({
        business_id: businessId,
        booking_id: bookingId || null,
        proposal_id: proposalId || null,
        project_id: projectId || null,
        gift_card_id: giftCardId || null,
        invoice_id: invoiceId || null,
        product_sale_id: productSaleId || null,
        amount: paymentAmount,
        method: selectedMethod.id as any,
        status: 'completed',
        reference: null,
        notes: null,
        payment_type: activeTab === 'deposit' ? 'deposit' : 'final'
      });

      console.log('✅ [UI DEBUG] Payment creation completed, result:', payment);
      console.log('✅ [UI DEBUG] PDF URL in result:', payment.pdf_url);

      await triggerSuccess(payment.id, paymentAmount, selectedMethod.name);
    } catch (error) {
      console.error('❌ [UI DEBUG] Payment creation failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateGiftCard = async () => {
    if (!giftCardCode.trim()) {
      setGiftCardError('Please enter a gift card code');
      return;
    }

    if (!bookingId) {
      setGiftCardError('Booking ID is required');
      return;
    }

    try {
      setValidatingGiftCard(true);
      setGiftCardError(null);

      const validation = await GiftCardService.validateForRedemption(
        businessId,
        giftCardCode.trim().toUpperCase()
      );

      if (!validation.isValid || !validation.giftCard) {
        setGiftCardError(validation.error || 'Invalid gift card');
        setValidatedGiftCard(null);
        setGiftCardBalance(null);
        return;
      }

      setValidatedGiftCard(validation.giftCard);
      setGiftCardBalance(validation.giftCard.current_balance);
      
      // Set max redemption amount based on active tab
      const remainingBalance = bookingTotal - amountPaid;
      let maxAmount = Math.min(validation.giftCard.current_balance, remainingBalance);
      
      // If deposit tab is active and deposit amount is set, use that as max
      if (activeTab === 'deposit' && depositAmount) {
        const depositAmountNum = parseFloat(depositAmount);
        maxAmount = Math.min(validation.giftCard.current_balance, depositAmountNum, remainingBalance);
      }
      
      setGiftCardRedemptionAmount(maxAmount.toFixed(2));
    } catch (error) {
      console.error('Error validating gift card:', error);
      setGiftCardError('Failed to validate gift card. Please try again.');
      setValidatedGiftCard(null);
      setGiftCardBalance(null);
    } finally {
      setValidatingGiftCard(false);
    }
  };

  const handleGiftCardRedemption = async () => {
    if (!validatedGiftCard || !bookingId) {
      setError('Gift card must be validated and booking ID is required');
      return;
    }

    const redemptionAmount = parseFloat(giftCardRedemptionAmount);
    if (isNaN(redemptionAmount) || redemptionAmount <= 0) {
      setError('Please enter a valid redemption amount');
      return;
    }

    const remainingBalance = bookingTotal - amountPaid;
    const maxAmount = activeTab === 'deposit' && depositAmount 
      ? Math.min(parseFloat(depositAmount), remainingBalance)
      : remainingBalance;
    
    if (redemptionAmount > maxAmount) {
      setError(`Redemption amount cannot exceed ${activeTab === 'deposit' ? 'deposit amount' : 'remaining balance'} ($${maxAmount.toFixed(2)})`);
      return;
    }

    if (redemptionAmount > (giftCardBalance || 0)) {
      setError(`Redemption amount cannot exceed gift card balance ($${giftCardBalance?.toFixed(2)})`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Redeem the gift card
      const redemptionResult = await GiftCardService.redeemGiftCard(
        businessId,
        validatedGiftCard.id,
        bookingId,
        redemptionAmount
      );

      if (!redemptionResult.success) {
        setError(redemptionResult.error || 'Failed to redeem gift card');
        return;
      }

      // Create payment record
      const payment = await createPayment({
        business_id: businessId,
        booking_id: bookingId || null,
        proposal_id: proposalId || null,
        project_id: projectId || null,
        gift_card_id: validatedGiftCard.id,
        invoice_id: invoiceId || null,
        product_sale_id: productSaleId || null,
        amount: redemptionAmount,
        method: 'other',
        channel: 'gift_card',
        status: 'completed',
        reference: `Gift Card: ${validatedGiftCard.code}`,
        notes: `Gift card redemption - ${validatedGiftCard.code}`,
        payment_type: activeTab === 'deposit' ? 'deposit' : 'final'
      });

      console.log('✅ Gift card payment created:', payment.id);
      await triggerSuccess(payment.id, redemptionAmount, `Gift Card ${validatedGiftCard.code}`);
    } catch (error) {
      console.error('Error processing gift card redemption:', error);
      setError(error instanceof Error ? error.message : 'Failed to process gift card redemption');
    } finally {
      setLoading(false);
    }
  };

  const handleStripePaymentLink = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      
      console.log('🔗 Creating payment record and generating payment link');

      // First create a payment record in the database
      const payment = await createPayment({
        business_id: businessId,
        booking_id: bookingId || null,
        proposal_id: proposalId || null,
        project_id: projectId || null,
        gift_card_id: giftCardId || null,
        invoice_id: invoiceId || null,
        product_sale_id: productSaleId || null,
        amount: parseFloat(amount),
        method: 'credit_card',
        channel: 'card_online',
        status: 'pending', // Will be updated to 'completed' when paid
        reference: null,
        notes: 'In-person payment',
        payment_type: activeTab === 'deposit' ? 'deposit' : 'final'
      });

      console.log('✅ Payment record created:', payment.id);

      // Generate payment link using the existing invoice system
      const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
        body: {
          paymentId: payment.id
        }
      });

      if (linkError) {
        throw new Error(linkError.message);
      }

      console.log('✅ Payment link generated:', linkData.paymentUrl);

      // Open payment link in new tab
      window.open(linkData.paymentUrl, '_blank');

      // Show success message and close modal
      // For Stripe payments, payment ID is available from the created payment
      await triggerSuccess(payment.id, parseFloat(amount) || 0, 'Stripe Payment Link');

    } catch (error) {
      console.error('❌ Failed to generate payment link:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate payment link');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = window.innerWidth < 640;
  const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: stackBackdropZ,
      }}
      onClick={onClose}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          zIndex: stackBackdropZ + 40,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)', WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {paymentSuccess ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 bg-white animate-payment-success-in">
              <div
                className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-5 animate-payment-success-check"
                aria-hidden="true"
              >
                <Check className="h-10 w-10 text-green-600" strokeWidth={3} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1 text-center" style={{ letterSpacing: '-0.02em' }}>
                {successMethodLabel.startsWith('Record ') ? 'Payment Recorded' : 'Payment Successful'}
              </h2>
              <p className="text-sm text-gray-500 text-center mb-1">
                {successMethodLabel || 'Payment recorded'}
              </p>
              <p className="text-4xl font-bold text-gray-900 tabular-nums mt-4 mb-10">
                ${successAmount.toFixed(2)}
              </p>

              <div className="w-full max-w-sm space-y-3">
                {onSendInvoice && (
                  <button
                    type="button"
                    onClick={() => {
                      onSendInvoice();
                      onClose();
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3.5 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] shadow-sm"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Send className="h-5 w-5" />
                    Send Invoice
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className={`w-full px-4 py-3.5 text-base font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] ${
                    onSendInvoice
                      ? 'text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300'
                      : 'text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 shadow-sm'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
          <>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="px-6 py-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Payment
            </h2>
            </div>
            
            {/* Tabs */}
            <div className="flex border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('payment');
                  const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
                  setAmount(remainingBalance.toFixed(2));
                  setDepositAmount('');
                  setSelectedPercentage(null);
                  setPaymentType(null);
                }}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'payment'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Payment
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab('deposit');
                  setAmount(depositAmount || '');
                  setSelectedPercentage(null);
                  setPaymentType(null);
                }}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'deposit'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Deposit
              </button>
            </div>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: paymentType ? (
                paymentType === 'wallet'
                  ? (walletRedemptionMode === 'custom'
                    ? (isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 380px)' : '380px')
                    : (isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 280px)' : '280px'))
                  : paymentType === 'gift_card' && !validatedGiftCard 
                  ? (isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 200px)' : '200px')
                  : paymentType === 'gift_card' && validatedGiftCard && giftCardBalance !== null
                  ? (isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 380px)' : '380px')
                  : (isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 180px)' : '180px')
              ) : undefined
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Deposit Amount Input - Only show when Deposit tab is active */}
              {activeTab === 'deposit' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Deposit Amount *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm font-medium">$</span>
                    </div>
                    <input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numValue = parseFloat(value);
                        const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
                        
                        if (value === '' || (numValue >= 0 && numValue <= remainingBalance)) {
                          setDepositAmount(value);
                          setAmount(value);
                          // Clear percentage selection when user manually types
                          setSelectedPercentage(null);
                        }
                      }}
                      min="0"
                      step="0.01"
                      max={Math.round((bookingTotal - amountPaid) * 100) / 100}
                      className={`w-full pl-8 pr-4 ${isMobile ? 'py-2.5' : 'py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation`}
                      placeholder="0.00"
                      style={{ 
                        fontSize: '16px', 
                        minHeight: isMobile ? '44px' : '48px',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
                        const amount = (remainingBalance * 0.25).toFixed(2);
                        setDepositAmount(amount);
                        setAmount(amount);
                        setSelectedPercentage('25');
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        selectedPercentage === '25'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      25%
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
                        const amount = (remainingBalance * 0.50).toFixed(2);
                        setDepositAmount(amount);
                        setAmount(amount);
                        setSelectedPercentage('50');
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        selectedPercentage === '50'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      50%
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
                        const amount = (remainingBalance * 0.75).toFixed(2);
                        setDepositAmount(amount);
                        setAmount(amount);
                        setSelectedPercentage('75');
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        selectedPercentage === '75'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      75%
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;
                        setDepositAmount(remainingBalance.toFixed(2));
                        setAmount(remainingBalance.toFixed(2));
                        setSelectedPercentage('full');
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        selectedPercentage === 'full'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      Full
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Remaining Balance: ${(Math.round((bookingTotal - amountPaid) * 100) / 100).toFixed(2)}
                  </p>
                </div>
              )}

              {/* Payment Method Cards — full grid until one is selected, then focused view with back button */}
              {(() => {
                const headlineAmount =
                  activeTab === 'deposit' && depositAmount
                    ? parseFloat(depositAmount).toFixed(2)
                    : remainingBalance.toFixed(2);

                type CardOption = {
                  key: NonNullable<typeof paymentType>;
                  label: string;
                  icon: React.ReactNode;
                  sublabel?: React.ReactNode;
                  badge?: React.ReactNode;
                  onSelect: () => void;
                  show: boolean;
                };

                const options: CardOption[] = [
                  {
                    key: 'manual',
                    label: 'Record Payment',
                    icon: <Receipt className="h-7 w-7" />,
                    sublabel: `$${headlineAmount}`,
                    show: true,
                    onSelect: () => {
                      setPaymentType('manual');
                      setSelectedMethod(manualPaymentMethods[0]);
                    },
                  },
                  {
                    key: 'stripe',
                    label: 'Stripe Pay',
                    icon: <CreditCard className="h-7 w-7" />,
                    sublabel: `$${headlineAmount}`,
                    badge: connectedReader && isNativePlatform ? (
                      <span
                        className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          paymentType === 'stripe'
                            ? 'bg-white/20 text-white'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        Reader Connected
                      </span>
                    ) : null,
                    show: stripeConnectEnabled,
                    onSelect: () => {
                      setPaymentType('stripe');
                      setIsDropdownOpen(false);
                      setReaderError(null);
                      setReaderDisplayMessage('');
                      if (!connectedReader) {
                        setReaderStatus('idle');
                        setDiscoveredReaders([]);
                        setReaderFlowOpen(false);
                      }
                    },
                  },
                  {
                    key: 'gift_card',
                    label: 'Gift Card',
                    icon: <Gift className="h-7 w-7" />,
                    show: giftCardAppInstalled,
                    onSelect: () => {
                      setPaymentType('gift_card');
                      setIsDropdownOpen(false);
                    },
                  },
                  {
                    key: 'wallet',
                    label: 'Wallet',
                    icon: <Wallet className="h-7 w-7" />,
                    sublabel: `$${walletBalance.toFixed(2)} available`,
                    show: walletBalance > 0 && !!customerId,
                    onSelect: () => {
                      setPaymentType('wallet');
                      setIsDropdownOpen(false);
                      const remaining = Math.round((bookingTotal - amountPaid) * 100) / 100;
                      const fullAmount = Math.min(walletBalance, remaining);
                      setWalletRedemptionMode('full');
                      setWalletRedemptionAmount(fullAmount.toFixed(2));
                    },
                  },
                ];

                const visibleOptions = options.filter((o) => o.show);
                const selected = visibleOptions.find((o) => o.key === paymentType);

                const cardBaseClass =
                  'relative flex flex-col items-center justify-center text-center gap-2 p-4 rounded-2xl border-2 transition-all duration-200 active:scale-[0.98]';
                const cardSelected = 'border-blue-600 bg-blue-600 text-white shadow-md';
                const cardUnselected =
                  'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 text-gray-800';

                const renderCard = (
                  o: CardOption,
                  isSelected: boolean,
                  fullWidth = false
                ) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={isSelected ? undefined : o.onSelect}
                    className={`${cardBaseClass} w-full ${
                      fullWidth ? 'min-h-[88px]' : 'min-h-[72px]'
                    } ${isSelected ? cardSelected : cardUnselected}`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {o.badge}
                    <span className="text-base font-semibold leading-tight">{o.label}</span>
                    {o.sublabel && (
                      <span
                        className={`text-xs font-medium ${
                          isSelected ? 'text-blue-100' : 'text-gray-500'
                        }`}
                      >
                        {o.sublabel}
                      </span>
                    )}
                  </button>
                );

                if (selected) {
                  return (
                    <div key="focused" className="space-y-3 animate-payment-focus-in">
                      <button
                        type="button"
                        onClick={() => {
                          setPaymentType(null);
                          setError(null);
                          setReaderError(null);
                          setGiftCardError(null);
                        }}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-blue-600 active:text-blue-700 transition-colors"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Change payment method
                      </button>
                      {renderCard(selected, true, true)}
                    </div>
                  );
                }

                return (
                  <div
                    key="grid"
                    className="grid grid-cols-1 gap-3 animate-payment-grid-in"
                  >
                    {visibleOptions.map((o) => renderCard(o, false))}
                  </div>
                );
              })()}

              {/* Payment Details - Clean Style */}
              {paymentType === 'wallet' ? (
                  <div className="space-y-4">
                    {/* Wallet redemption info will be in footer */}
                  </div>
                ) : paymentType === 'gift_card' ? (
                  <div className="space-y-4">
                    {/* Empty space - gift card info and redemption amount will be in footer */}
                  </div>
                ) : paymentType === 'stripe' ? (
                  <div className="space-y-3">
                    {/* In-person Stripe methods (iOS app): Tap to Pay charges on this
                        phone immediately; Card Reader connects a WisePad on demand.
                        Both are Stripe Terminal — the card entry below stays the
                        default for typed/keyed payments. */}
                    {isNativePlatform && (
                      <div className="grid gap-3 grid-cols-2">
                        {tapToPayAvailable ? (
                          <button
                            type="button"
                            onClick={handleTapToPayCharge}
                            disabled={tapCharging || loading}
                            className={`relative flex flex-col items-center justify-center text-center gap-2 p-4 rounded-2xl border-2 transition-all duration-200 active:scale-[0.98] min-h-[88px] disabled:cursor-not-allowed ${
                              tapCharging
                                ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                                : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 text-gray-800'
                            }`}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            {tapCharging ? (
                              <>
                                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span className="text-sm font-semibold leading-tight">Hold card near iPhone…</span>
                              </>
                            ) : (
                              <>
                                <Smartphone className="h-6 w-6" />
                                <span className="text-base font-semibold leading-tight">Tap to Pay</span>
                                <span className="text-xs font-medium text-gray-500">Charge on this iPhone</span>
                              </>
                            )}
                          </button>
                        ) : (
                          /* Tap to Pay is temporarily unavailable (live scope pending) —
                             keep the tile visible so staff know it's on the way. */
                          <div className="relative flex flex-col items-center justify-center text-center gap-2 p-4 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 text-gray-400 min-h-[88px]">
                            <Smartphone className="h-6 w-6" />
                            <span className="text-base font-semibold leading-tight text-gray-500">Tap to Pay</span>
                            <span className="text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-2.5 py-0.5">Coming soon</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={connectedReader ? undefined : handleConnectReader}
                          disabled={readerStatus === 'discovering' || readerStatus === 'connecting'}
                          className={`relative flex flex-col items-center justify-center text-center gap-2 p-4 rounded-2xl border-2 transition-all duration-200 active:scale-[0.98] min-h-[88px] disabled:cursor-not-allowed ${
                            connectedReader
                              ? 'border-green-500 bg-green-50 text-gray-800'
                              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 text-gray-800'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {connectedReader ? (
                            <>
                              <span className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                                <Bluetooth className="h-5 w-5 text-green-600" />
                              </span>
                              <span className="text-base font-semibold leading-tight">
                                {connectedReader.label || connectedReader.serialNumber}
                              </span>
                              <span className="text-xs font-medium text-green-700">Reader connected</span>
                            </>
                          ) : readerStatus === 'discovering' || readerStatus === 'connecting' ? (
                            <>
                              <div className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
                              <span className="text-sm font-semibold leading-tight text-blue-700">
                                {readerStatus === 'discovering' ? 'Finding reader…' : 'Connecting…'}
                              </span>
                            </>
                          ) : (
                            <>
                              <Bluetooth className="h-6 w-6" />
                              <span className="text-base font-semibold leading-tight">Card Reader</span>
                              <span className="text-xs font-medium text-gray-500">
                                {readerError ? 'Not found — tap to retry' : 'Connect a WisePad'}
                              </span>
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {/* Reader status detail (picker, progress, errors) — only once
                        the reader flow has actually been started from the card. */}
                    {isNativePlatform && (() => {
                      const readerActive =
                        !!connectedReader ||
                        readerStatus !== 'idle' ||
                        discoveredReaders.length > 0 ||
                        !!readerError ||
                        firmwareUpdating ||
                        readerFlowOpen;

                      if (!readerActive) return null;

                      return (
                        <div className="space-y-3 animate-fade-in">
                          {firmwareUpdating && (
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-blue-900">Updating reader firmware</span>
                                <span className="text-sm font-bold text-blue-900 tabular-nums">{firmwareProgress ?? 0}%</span>
                              </div>
                              <div className="w-full h-2 bg-blue-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-600 transition-all duration-500"
                                  style={{ width: `${firmwareProgress ?? 0}%` }}
                                />
                              </div>
                              <p className="text-xs text-blue-800 mt-2">
                                First-time setup can take 10–15 minutes. Keep the reader nearby, powered on, and the app open. Do not close this screen.
                              </p>
                            </div>
                          )}

                          {connectedReader ? (
                            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-xl">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                                <div>
                                  <p className="text-sm font-semibold text-green-800">
                                    {connectedReader.label || connectedReader.serialNumber}
                                  </p>
                                  <p className="text-xs text-green-700">Reader connected</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={async () => {
                                  await readerService.disconnectCurrentReader();
                                  setConnectedReader(null);
                                  setDiscoveredReaders([]);
                                  setReaderError(null);
                                  setReaderFlowOpen(false);
                                }}
                                className="text-xs text-red-500 font-medium hover:text-red-700"
                              >
                                Disconnect
                              </button>
                            </div>
                          ) : (
                            <>
                              {readerStatus === 'discovering' && (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 text-sm text-blue-600">
                                    <div className="w-4 h-4 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
                                    Finding your reader…
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      readerService.cancelReaderDiscovery();
                                      setReaderStatus('idle');
                                      setDiscoveredReaders([]);
                                      setReaderError('Search cancelled.');
                                    }}
                                    className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    Cancel search
                                  </button>
                                </div>
                              )}

                              {readerStatus === 'connecting' && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between gap-2 text-sm text-blue-600">
                                    <div className="flex items-center gap-2">
                                      <div className="w-4 h-4 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
                                      Connecting to reader...
                                    </div>
                                    <span className="text-xs text-gray-500 tabular-nums">
                                      {Math.floor(connectElapsedSec / 60)}:{String(connectElapsedSec % 60).padStart(2, '0')}
                                    </span>
                                  </div>
                                  {readerDisplayMessage && (
                                    <div className="text-xs text-gray-700 bg-blue-50 p-3 rounded-lg">
                                      {readerDisplayMessage}
                                    </div>
                                  )}
                                  {connectElapsedSec > 30 && (
                                    <div className="text-xs text-amber-700 bg-amber-50 p-3 rounded-lg">
                                      First-time setup can take up to <strong>15 minutes</strong> for the firmware update. Keep the reader powered on and within range. The reader's screen may show progress.
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      await readerService.disconnectCurrentReader();
                                      setConnectedReader(null);
                                      setReaderStatus('idle');
                                      setDiscoveredReaders([]);
                                      setReaderDisplayMessage('');
                                      setReaderError('Connection cancelled.');
                                      setConnectStartedAt(null);
                                    }}
                                    className="w-full px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-all"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    Cancel connection
                                  </button>
                                </div>
                              )}

                              {discoveredReaders.length > 0 && readerStatus !== 'connecting' && (
                                <div className="space-y-2">
                                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Available Readers</p>
                                  {discoveredReaders.map((reader) => (
                                    <button
                                      key={reader.serialNumber}
                                      type="button"
                                      onClick={async () => {
                                        setReaderError(null);
                                        setConnectStartedAt(Date.now());
                                        try {
                                          await readerService.connectReader(businessId, reader);
                                          setConnectedReader(readerService.getConnectedReader());
                                        } catch (err: any) {
                                          setReaderError(err?.message || 'Connection failed');
                                          setConnectedReader(null);
                                        } finally {
                                          setConnectStartedAt(null);
                                        }
                                      }}
                                      className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                                      style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                      <div className="flex items-center gap-3">
                                        <Bluetooth className="h-4 w-4 text-blue-500" />
                                        <div className="text-left">
                                          <p className="text-sm font-medium text-gray-900">{reader.label || reader.serialNumber}</p>
                                          <p className="text-xs text-gray-500">{reader.deviceType}</p>
                                        </div>
                                      </div>
                                      <ChevronRight className="h-4 w-4 text-gray-400" />
                                    </button>
                                  ))}
                                </div>
                              )}

                              {readerStatus === 'idle' && discoveredReaders.length === 0 && !readerError && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    setReaderError(null);
                                    setDiscoveredReaders([]);
                                    try {
                                      const readers = await readerService.discoverReaders(businessId);
                                      setDiscoveredReaders(readers);
                                      if (readers.length === 0) {
                                        setReaderError(
                                          'No readers found. Check: (1) WisePad 3 is on and charged, (2) iPhone Bluetooth is on, (3) Settings → Privacy & Security → Bluetooth → enable this app, (4) reader is registered in Stripe Dashboard → Terminal → Readers for your Connect account, (5) reader is not connected to another phone.'
                                        );
                                      }
                                    } catch (err: any) {
                                      setReaderError(err?.message || 'Failed to discover readers');
                                    } finally {
                                      setReaderStatus('idle');
                                    }
                                  }}
                                  className="w-full px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all flex items-center justify-center"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <Bluetooth className="h-4 w-4 mr-2" />
                                  Find Readers
                                </button>
                              )}

                              {readerError && (
                                <div className="space-y-2">
                                  <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                    <span>{readerError}</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setReaderFlowOpen(false);
                                      setReaderError(null);
                                      setDiscoveredReaders([]);
                                    }}
                                    className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    Close
                                  </button>
                                </div>
                              )}
                            </>
                          )}

                          {connectedReader && readerDisplayMessage && (readerStatus === 'waiting_for_card' || readerStatus === 'processing' || readerStatus === 'confirming') && (
                            <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-lg">
                              <div className="w-4 h-4 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
                              {readerDisplayMessage}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ) : paymentType === 'manual' ? (
                  <div className="space-y-4">
                    {/* Empty space - payment method will be in footer */}
                  </div>
                ) : null}
            </div>
          </form>

          {/* Fixed Footer with Pay Button */}
          {paymentType && (
          <div className="border-t border-gray-200 bg-white" style={{
            paddingTop: isMobile ? '12px' : '16px',
            paddingBottom: isMobile ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : '16px',
            paddingLeft: isMobile ? '16px' : '24px',
            paddingRight: isMobile ? '16px' : '24px'
          }}>
            {/* Payment Method Selection - Only for manual payments */}
            {paymentType === 'manual' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-center">
                      {selectedMethod.icon}
                      <span className="ml-3 text-base font-medium text-gray-900">{selectedMethod.name}</span>
                    </div>
                    <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`} />
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden">
                      {manualPaymentMethods.map((method) => (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => {
                            setSelectedMethod(method);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full flex items-center px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors ${
                            selectedMethod.id === method.id ? 'bg-blue-50' : ''
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {method.icon}
                          <span className="ml-3 text-sm font-medium text-gray-900">{method.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Wallet Redemption UI */}
            {paymentType === 'wallet' && (
              <div className="mb-4 space-y-3">
                <div className="bg-white border border-gray-300 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">Wallet Balance</span>
                    <span className="text-lg font-bold text-gray-900">${walletBalance.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Remaining Due</span>
                    <span className="text-sm font-semibold text-gray-600">${(Math.round((bookingTotal - amountPaid) * 100) / 100).toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setWalletRedemptionMode('full');
                      const remaining = Math.round((bookingTotal - amountPaid) * 100) / 100;
                      setWalletRedemptionAmount(Math.min(walletBalance, remaining).toFixed(2));
                    }}
                    className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-xl transition-colors ${
                      walletRedemptionMode === 'full'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Full Balance (${Math.min(walletBalance, Math.round((bookingTotal - amountPaid) * 100) / 100).toFixed(2)})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setWalletRedemptionMode('custom');
                      setWalletRedemptionAmount('');
                    }}
                    className={`flex-1 px-3 py-2.5 text-sm font-semibold rounded-xl transition-colors ${
                      walletRedemptionMode === 'custom'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Custom Amount
                  </button>
                </div>

                {walletRedemptionMode === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount to Redeem *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm font-medium">$</span>
                      </div>
                      <input
                        type="number"
                        value={walletRedemptionAmount}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numValue = parseFloat(value);
                          const remaining = Math.round((bookingTotal - amountPaid) * 100) / 100;
                          const maxAmount = Math.min(walletBalance, remaining);
                          if (value === '' || (numValue >= 0 && numValue <= maxAmount)) {
                            setWalletRedemptionAmount(value);
                          }
                        }}
                        min="0"
                        step="0.01"
                        max={Math.min(walletBalance, Math.round((bookingTotal - amountPaid) * 100) / 100)}
                        className={`w-full pl-8 pr-4 ${isMobile ? 'py-2.5' : 'py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation`}
                        placeholder="0.00"
                        style={{ fontSize: '16px', minHeight: isMobile ? '44px' : '48px', WebkitTapHighlightColor: 'transparent' }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Max: ${Math.min(walletBalance, Math.round((bookingTotal - amountPaid) * 100) / 100).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Gift Card Code Input - Only for gift card payments, before validation */}
            {paymentType === 'gift_card' && !validatedGiftCard && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gift Card Code *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={giftCardCode}
                    onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleValidateGiftCard();
                      }
                    }}
                    placeholder="GIFT-XXXX-XXXX-XXXX"
                    className={`flex-1 ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-mono touch-manipulation`}
                    style={{ 
                      fontSize: '16px', 
                      minHeight: isMobile ? '44px' : '48px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    disabled={validatingGiftCard}
                  />
                  <button
                    type="button"
                    onClick={handleValidateGiftCard}
                    disabled={validatingGiftCard || !giftCardCode.trim()}
                    className={`${isMobile ? 'px-4 py-2.5 text-[13px]' : 'px-6 py-3 text-base'} font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                      (!validatingGiftCard && giftCardCode.trim())
                        ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' 
                        : 'bg-gray-300 cursor-not-allowed'
                    } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {validatingGiftCard ? (
                      <div className="flex items-center">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        Validating...
                      </div>
                    ) : (
                      'Validate'
                    )}
                  </button>
                </div>
                {giftCardError && (
                  <div className="mt-2 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {giftCardError}
                  </div>
                )}
              </div>
            )}

            {/* Gift Card Info Display and Redemption Amount - Only after validation */}
            {paymentType === 'gift_card' && validatedGiftCard && giftCardBalance !== null && (
              <>
                {/* Gift Card Info Display */}
                <div className="mb-4">
                  <div className="bg-white border border-gray-300 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Gift Card Code:</span>
                      <span className="text-sm font-mono font-semibold text-gray-900">{validatedGiftCard.code}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Available Balance:</span>
                      <span className="text-lg font-bold text-gray-900">${giftCardBalance.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Redemption Amount Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Redeem *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm font-medium">$</span>
                    </div>
                    <input
                      type="number"
                      value={giftCardRedemptionAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numValue = parseFloat(value);
                        const remainingBalance = bookingTotal - amountPaid;
                        const maxAmount = Math.min(giftCardBalance || 0, remainingBalance);
                        
                        if (value === '' || (numValue >= 0 && numValue <= maxAmount)) {
                          setGiftCardRedemptionAmount(value);
                        }
                      }}
                      min="0"
                      step="0.01"
                      max={Math.min(giftCardBalance || 0, bookingTotal - amountPaid)}
                      className={`w-full pl-8 pr-4 ${isMobile ? 'py-2.5' : 'py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation`}
                      placeholder="0.00"
                      style={{ 
                        fontSize: '16px', 
                        minHeight: isMobile ? '44px' : '48px',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                    />
                  </div>
                  <div className="mt-2">
                    <p className="text-xs text-gray-600">
                      Maximum: ${Math.min(giftCardBalance || 0, bookingTotal - amountPaid).toFixed(2)} 
                      {' '}(based on gift card balance and remaining due)
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Stripe: saved card on file (above manual card entry) - hidden when reader is connected */}
            {paymentType === 'stripe' && customerId && !connectedReader && (
              <div className="mb-4">
                {savedCardLoading ? (
                  <p className="text-sm text-gray-500">Checking for a saved card…</p>
                ) : savedCardOnFile ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50/90 p-4 space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Card on file</p>
                      <p className="text-sm font-semibold text-gray-900 capitalize mt-1">
                        {(savedCardOnFile.card_brand || 'Card').replace(/_/g, ' ')} •••• {savedCardOnFile.card_last4}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Exp{' '}
                        {savedCardOnFile.card_exp_month != null && savedCardOnFile.card_exp_year != null
                          ? `${String(savedCardOnFile.card_exp_month).padStart(2, '0')}/${String(
                              savedCardOnFile.card_exp_year,
                            ).slice(-2)}`
                          : '—'}
                      </p>
                    </div>
                    {savedCardChargePreview.subline && (
                      <p className="text-xs text-gray-600 leading-relaxed">{savedCardChargePreview.subline}</p>
                    )}
                    <button
                      type="button"
                      onClick={handleChargeSavedCard}
                      aria-busy={savedCardCharging}
                      disabled={
                        savedCardCharging ||
                        loading ||
                        !amount ||
                        parseFloat(amount) <= 0 ||
                        (activeTab === 'deposit' && (!depositAmount || parseFloat(depositAmount) <= 0))
                      }
                      className="w-full px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none flex items-center justify-center gap-2 text-center"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      {savedCardCharging ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin shrink-0" />
                          <span>Charging card…</span>
                        </>
                      ) : (
                        <span className="leading-snug">{savedCardChargePreview.buttonLabel}</span>
                      )}
                    </button>
                    <p className="text-xs text-center text-gray-500">Or enter a different card below</p>
                  </div>
                ) : null}
              </div>
            )}

            {/* Stripe Card Entry - Only for stripe payments without a reader connected */}
            {paymentType === 'stripe' && !connectedReader && (
              <div className="mb-4">
                <StripeCardEntry
                  amount={activeTab === 'deposit' && depositAmount 
                    ? parseFloat(depositAmount) || 0 
                    : parseFloat(amount) || 0}
                  businessId={businessId}
                  bookingId={bookingId || undefined}
                  proposalId={proposalId || undefined}
                  payment_type={activeTab === 'deposit' ? 'deposit' : 'final'}
                  surchargeEnabled={surchargeEnabled}
                  surchargePercentage={surchargePercentage}
                  customerId={customerId}
                  saveCardOnFile={saveNewCardOnFile}
                  onSaveCardOnFileChange={setSaveNewCardOnFile}
                  onSuccess={async (paymentId) => {
                    console.log('✅ Payment successful:', paymentId);
                    const chargedAmount = activeTab === 'deposit' && depositAmount
                      ? parseFloat(depositAmount) || 0
                      : parseFloat(amount) || 0;
                    await triggerSuccess(paymentId, chargedAmount, 'Stripe Pay');
                  }}
                  onError={(errorMessage) => {
                    console.error('❌ Payment error:', errorMessage);
                    setError(errorMessage);
                  }}
                  onProcessingChange={(isProcessing) => {
                    setLoading(isProcessing);
                  }}
                />
              </div>
            )}

            {/* Stripe Pay with connected reader: Send to Reader button replaces the card entry */}
            {paymentType === 'stripe' && connectedReader && (() => {
              const baseAmount = getStripeBaseChargeAmount();
              const pct = surchargePercentage || 0;
              const surchargeOn = surchargeEnabled && pct > 0;
              const surchargeFee = surchargeOn
                ? Math.round(baseAmount * (pct / 100) * 100) / 100
                : 0;
              const creditTotal = Math.round((baseAmount + surchargeFee) * 100) / 100;
              const debitTotal = Math.round(baseAmount * 100) / 100;

              const requiresSelection = surchargeOn;
              const selectionMade = !requiresSelection || readerCardType !== null;
              const finalSurcharge = requiresSelection && readerCardType === 'credit' ? surchargeFee : 0;
              const finalChargeAmount = Math.round((baseAmount + finalSurcharge) * 100) / 100;
              const sendLabel = selectionMade
                ? `Send $${finalChargeAmount.toFixed(2)} to Reader`
                : 'Select Debit or Credit';

              return (
                <div className="mb-4 space-y-3">
                  {requiresSelection && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Card Type</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setReaderCardType('debit')}
                          disabled={loading || readerStatus === 'waiting_for_card' || readerStatus === 'processing' || readerStatus === 'confirming'}
                          className={`flex flex-col items-center justify-center text-center gap-1 p-3 rounded-xl border-2 transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${
                            readerCardType === 'debit'
                              ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 text-gray-800'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <span className="text-sm font-semibold leading-tight">Debit</span>
                          <span className={`text-base font-bold tabular-nums ${readerCardType === 'debit' ? 'text-white' : 'text-gray-900'}`}>
                            ${debitTotal.toFixed(2)}
                          </span>
                          <span className={`text-[10px] font-medium ${readerCardType === 'debit' ? 'text-blue-100' : 'text-gray-500'}`}>
                            No processing fee
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setReaderCardType('credit')}
                          disabled={loading || readerStatus === 'waiting_for_card' || readerStatus === 'processing' || readerStatus === 'confirming'}
                          className={`flex flex-col items-center justify-center text-center gap-1 p-3 rounded-xl border-2 transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${
                            readerCardType === 'credit'
                              ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 text-gray-800'
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <span className="text-sm font-semibold leading-tight">Credit</span>
                          <span className={`text-base font-bold tabular-nums ${readerCardType === 'credit' ? 'text-white' : 'text-gray-900'}`}>
                            ${creditTotal.toFixed(2)}
                          </span>
                          <span className={`text-[10px] font-medium ${readerCardType === 'credit' ? 'text-blue-100' : 'text-gray-500'}`}>
                            +${surchargeFee.toFixed(2)} processing fee
                          </span>
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={async () => {
                      if (!baseAmount || baseAmount <= 0) {
                        setError('Enter a valid amount');
                        return;
                      }
                      if (requiresSelection && !readerCardType) {
                        setError('Select Debit or Credit before sending to reader');
                        return;
                      }
                      setLoading(true);
                      setError(null);
                      setReaderError(null);
                      try {
                        const { paymentIntentId } = await readerService.processReaderPayment({
                          businessId,
                          amount: finalChargeAmount,
                          paymentType: activeTab === 'deposit' ? 'deposit' : 'final',
                          bookingId: bookingId || undefined,
                          proposalId: proposalId || undefined,
                          description: 'Card reader payment',
                        });
                        const readerCardTypeLabel = requiresSelection
                          ? readerCardType === 'credit'
                            ? 'Credit'
                            : 'Debit'
                          : null;
                        const noteParts = ['Card Reader (WisePad 3)'];
                        if (readerCardTypeLabel) noteParts.push(readerCardTypeLabel);
                        if (finalSurcharge > 0) noteParts.push(`includes $${finalSurcharge.toFixed(2)} processing fee`);
                        const { data: payment, error: paymentError } = await supabase
                          .from('payments')
                          .insert({
                            business_id: businessId,
                            booking_id: bookingId || null,
                            proposal_id: proposalId || null,
                            project_id: projectId || null,
                            invoice_id: invoiceId || null,
                            product_sale_id: productSaleId || null,
                            amount: finalChargeAmount,
                            base_amount: baseAmount,
                            surcharge_amount: finalSurcharge,
                            method: 'credit_card',
                            channel: 'card_reader',
                            status: 'completed',
                            stripe_payment_intent_id: paymentIntentId,
                            reference: paymentIntentId,
                            notes: noteParts.join(' — '),
                            payment_type: activeTab === 'deposit' ? 'deposit' : 'final',
                          })
                          .select()
                          .single();
                        if (paymentError) {
                          console.error('Failed to create payment record:', paymentError);
                          setError('Payment succeeded but failed to save the record. Please refresh and verify.');
                        } else {
                          const cardLabel = requiresSelection && readerCardType
                            ? `Card Reader (${readerCardType === 'credit' ? 'Credit' : 'Debit'})`
                            : 'Card Reader (WisePad 3)';
                          await triggerSuccess(payment.id, finalChargeAmount, cardLabel);
                        }
                      } catch (err: any) {
                        const msg = err?.message || 'Card reader payment failed';
                        setReaderError(msg);
                        setError(msg);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={
                      loading ||
                      baseAmount <= 0 ||
                      !selectionMade ||
                      readerStatus === 'waiting_for_card' ||
                      readerStatus === 'confirming'
                    }
                    className="w-full px-4 py-3.5 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {loading || readerStatus === 'waiting_for_card' || readerStatus === 'processing' || readerStatus === 'confirming' ? (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3" />
                        {readerStatus === 'waiting_for_card' ? 'Waiting for card...' :
                         readerStatus === 'confirming' ? 'Confirming...' : 'Processing...'}
                      </div>
                    ) : (
                      sendLabel
                    )}
                  </button>
                  {loading && readerStatus === 'waiting_for_card' && (
                    <button
                      type="button"
                      onClick={async () => {
                        await readerService.cancelPayment();
                        setLoading(false);
                      }}
                      className="w-full px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-all"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              );
            })()}
            
            {/* Pay Button - Only show when appropriate */}
            {paymentType === 'wallet' ? (
              <button
                type="button"
                onClick={handleWalletRedemption}
                disabled={loading || !walletRedemptionAmount || parseFloat(walletRedemptionAmount) <= 0}
                className={`w-full ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-6 py-3.5 text-base'} font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${
                  (!loading && walletRedemptionAmount && parseFloat(walletRedemptionAmount) > 0)
                    ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                    : 'bg-gray-300 cursor-not-allowed'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                    Processing...
                  </div>
                ) : (
                  `Redeem Wallet - $${walletRedemptionAmount || '0.00'}`
                )}
              </button>
            ) : paymentType === 'gift_card' && validatedGiftCard && giftCardBalance !== null ? (
              <button
                type="button"
                onClick={handleGiftCardRedemption}
                disabled={loading || !giftCardRedemptionAmount || parseFloat(giftCardRedemptionAmount) <= 0}
                className={`w-full ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-6 py-3.5 text-base'} font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${
                  (!loading && giftCardRedemptionAmount && parseFloat(giftCardRedemptionAmount) > 0)
                    ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' 
                    : 'bg-gray-300 cursor-not-allowed'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                    Processing...
                  </div>
                ) : (
                  `Redeem Gift Card - $${giftCardRedemptionAmount || '0.00'}`
                )}
              </button>
            ) : paymentType === 'gift_card' && !validatedGiftCard ? (
              // No button shown when gift card is selected but not validated
              null
            ) : paymentType === 'stripe' ? (
              // Stripe payment button is handled inside StripeCardEntry (or Send-to-Reader block above)
              null
            ) : paymentType === 'manual' ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e as any);
                }}
                disabled={
                  loading || 
                  !amount || 
                  parseFloat(amount) <= 0 ||
                  (activeTab === 'deposit' && (!depositAmount || parseFloat(depositAmount) <= 0))
                }
                className="w-full px-4 py-3.5 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                    Processing...
                  </div>
                ) : (
                  `Record ${activeTab === 'deposit' ? 'Deposit' : 'Payment'} $${activeTab === 'deposit' && depositAmount ? depositAmount : amount}`
                )}
              </button>
            ) : null}
          </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}